import { useRef, useCallback, useEffect, useImperativeHandle, useState } from 'preact/hooks'
import { forwardRef } from 'preact/compat'
import UnlayerEditor from 'react-email-editor'
import type { EditorRef as UnlayerEditorRef } from 'react-email-editor'
import { Loader2 } from 'lucide-preact'
import { useCompany } from '@/hooks/useCompany'
import { useToast } from '@/hooks/useToast'
import {
    assetApi,
    MAX_EMAIL_IMAGE_BYTES,
    ALLOWED_EMAIL_IMAGE_MIME_TYPES,
} from '@/api/asset'

// ORB-290: parsed once at module load. Unlayer's projectId is documented as an integer;
// Vite env vars are strings, so coerce here. Missing or unparseable falls back to free-tier
// behavior (image uploads route to assets.unlayer.com) — surfaced as a one-time dev warning
// so a forgotten env var doesn't silently degrade the editor.
const UNLAYER_PROJECT_ID: number | undefined = (() => {
    const raw = import.meta.env.VITE_UNLAYER_PROJECT_ID
    if (!raw) return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
})()
if (!UNLAYER_PROJECT_ID && import.meta.env.DEV) {
     
    console.warn('[UnlayerDesigner] VITE_UNLAYER_PROJECT_ID is not set — image uploads will fall back to assets.unlayer.com')
}

// Note on Preact + Unlayer:
// The package's React wrapper just mounts a host div, loads
// https://editor.unlayer.com/embed.js, and the script creates an iframe pointing at
// editor.unlayer.com where the real editor runs. Communication happens via
// postMessage. Because the editor runs INSIDE the iframe (their React stack, not ours),
// our Preact compatibility issues don't apply here.

export interface UnlayerDesignerHandle {
    /** Returns the current design as a JSON object (Unlayer's proprietary shape). */
    getDesign: () => Promise<unknown>
    /** Compiles the current design to HTML inside the iframe and returns it. */
    getHtml: () => Promise<string>
}

export interface UnlayerMergeTag {
    name: string
    value: string
    sample: string
}

export type UnlayerMergeTags = Record<string, UnlayerMergeTag>

interface UnlayerDesignerProps {
    /** Optional design JSON to load when the editor is ready. If omitted, the editor
     *  starts blank. */
    initialDesign?: unknown
    /** Fires on every design change. Receives both the updated design JSON and the
     *  freshly-compiled HTML — caller persists whichever it wants. */
    onUpdate?: (design: unknown, html: string) => void
    /** Merge tag pills exposed in Unlayer's UI. Templates and copies surface different
     *  sets — templates include the slot markers, copies expose only recipient and
     *  web-entity variables. */
    mergeTags: UnlayerMergeTags
    editable?: boolean
    className?: string
}

/**
 * Shared Unlayer iframe wrapper used by both the Email Template and Email Copy editors.
 * Exposes a forwardRef handle (getDesign, getHtml) plus an onUpdate callback so the
 * consumer can auto-save the design JSON (canonical, editable source) and the compiled
 * HTML (consumed at render/send time) on every change.
 */
export const UnlayerDesigner = forwardRef<UnlayerDesignerHandle, UnlayerDesignerProps>(
    function UnlayerDesigner({ initialDesign, onUpdate, mergeTags, editable = true, className }, ref) {
        const editorRef = useRef<UnlayerEditorRef>(null)
        const wrapperRef = useRef<HTMLDivElement>(null)
        const [ready, setReady] = useState(false)
        // Unlayer's iframe needs an explicit pixel height — `minHeight="100%"` does not
        // propagate through the iframe boundary. We measure the wrapping div and pass the
        // pixel value as `minHeight`. ResizeObserver keeps it in sync if the window resizes
        // or the side panels change width.
        const [measuredHeight, setMeasuredHeight] = useState<number>(600)
        const initialDesignRef = useRef(initialDesign)
        initialDesignRef.current = initialDesign

        // ORB-290: ambient context the image-upload callback needs. handleLoad fires once
        // when the Unlayer iframe initializes, so the callback closure can't read changing
        // hook values directly — refs let us pick up the latest workspace + capability state
        // at the moment a user actually drops a file in.
        const { companyId, currentWorkspaceId } = useCompany()
        const { showError } = useToast()
        const editableRef = useRef(editable)
        editableRef.current = editable
        const companyIdRef = useRef(companyId)
        companyIdRef.current = companyId
        const workspaceIdRef = useRef(currentWorkspaceId)
        workspaceIdRef.current = currentWorkspaceId

        useEffect(() => {
            const el = wrapperRef.current
            if (!el) return
            const observer = new ResizeObserver(entries => {
                const entry = entries[0]
                if (!entry) return
                const next = Math.max(400, Math.round(entry.contentRect.height))
                setMeasuredHeight(next)
            })
            observer.observe(el)
            return () => observer.disconnect()
        }, [])

        // Promise wrappers around Unlayer's callback-style APIs so the parent's auto-save
        // pipeline can `await` them like our existing EmailEditor.
        const getDesignAsync = useCallback((): Promise<unknown> => {
            return new Promise((resolve) => {
                const editor = editorRef.current?.editor
                if (!editor) return resolve(null)
                editor.saveDesign((design) => resolve(design))
            })
        }, [])

        const getHtmlAsync = useCallback((): Promise<string> => {
            return new Promise((resolve) => {
                const editor = editorRef.current?.editor
                if (!editor) return resolve('')
                editor.exportHtml((data) => resolve(data.html))
            })
        }, [])

        useImperativeHandle(ref, () => ({
            getDesign: getDesignAsync,
            getHtml: getHtmlAsync,
        }), [getDesignAsync, getHtmlAsync])

        // ORB-290: handler Unlayer invokes when a user picks an image. Validates locally
        // against the same caps the server enforces (10 MB, jpg/png/gif/webp) so an obviously
        // bad file doesn't burn a multipart round-trip, then POSTs to the email-images
        // endpoint. On 4xx we toast the server's message in addition to letting Unlayer show
        // its own inline error; on 5xx Unlayer's generic inline error is enough.
        const handleImageUpload = useCallback(async (
            upload: { attachments?: FileList | File[] },
            done: (result: { progress: number; url?: string }) => void,
        ) => {
            const companyId = companyIdRef.current
            const workspaceId = workspaceIdRef.current
            const file = upload.attachments?.[0]

            if (!file) {
                done({ progress: 100 })
                return
            }

            if (!companyId || !workspaceId) {
                showError('Upload failed', 'Workspace context is missing. Refresh the page and try again.')
                done({ progress: 100 })
                return
            }

            if (file.size > MAX_EMAIL_IMAGE_BYTES) {
                const mb = Math.round(MAX_EMAIL_IMAGE_BYTES / 1024 / 1024)
                showError('Image too large', `Maximum size is ${mb} MB.`)
                done({ progress: 100 })
                return
            }

            const allowed: readonly string[] = ALLOWED_EMAIL_IMAGE_MIME_TYPES
            if (!allowed.includes(file.type)) {
                showError('Unsupported image type', 'Please upload a JPEG, PNG, GIF, or WEBP image.')
                done({ progress: 100 })
                return
            }

            try {
                const resp = await assetApi.uploadEmailImage(companyId, workspaceId, file)
                if (resp.data?.url) {
                    done({ progress: 100, url: resp.data.url })
                    return
                }

                if (resp.status >= 400 && resp.status < 500) {
                    showError('Upload failed', resp.error || 'The server rejected the image.')
                }
            } catch {
                // apiClient.request is contract-bound to never reject (it maps network
                // errors to ApiResponse), but belt-and-suspenders here keeps Unlayer's
                // picker from hanging in an indefinite uploading state if that contract
                // ever changes (e.g., an interceptor introduces a synchronous throw).
                showError('Upload failed', 'Unexpected error during upload.')
            }
            done({ progress: 100 })
        }, [showError])

        // Unlayer's lifecycle has two events:
        //   - onLoad: editor's JS is loaded and ready for setup (loadDesign, listeners)
        //   - onReady: editor is fully initialized AFTER any initial design has loaded
        //
        // Per Unlayer's official demo, `loadDesign` MUST be called inside `onLoad`.
        // Calling it later conflicts with the editor's internal blank-state initialization
        // and produces cryptic schema migration errors.
        //
        // Auto-save gating: `design:updated` ALSO fires as part of loadDesign's own internal
        // mechanics, not just on user edits. We arm auto-save only AFTER `design:loaded`
        // plus a 500ms grace window — otherwise Unlayer's settling transactions get captured
        // as edits and a blank canvas overwrites the stored content. If no design is ever
        // loaded (blank-canvas creation), saves stay disabled and a manual-save path on the
        // ref handles persistence.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleLoad = useCallback((unlayer: any) => {
            let userEditsEnabled = false

            unlayer.addEventListener('design:loaded', () => {
                setTimeout(() => { userEditsEnabled = true }, 500)
            })

            unlayer.addEventListener('design:updated', () => {
                if (!userEditsEnabled) return
                if (!onUpdate) return
                Promise.all([getDesignAsync(), getHtmlAsync()])
                    .then(([design, html]) => onUpdate(design, html))
                    .catch(() => { /* export failures here are non-fatal — next edit will retry */ })
            })

            // ORB-290: only wire the upload affordance when editing is permitted. The shield
            // overlay already blocks pointer events in the locked Asset Review surface, but
            // gating the callback too is defense-in-depth in case Unlayer ever surfaces an
            // upload entry point the shield doesn't fully cover.
            if (editableRef.current) {
                unlayer.registerCallback('image', handleImageUpload)
            }

            if (initialDesignRef.current) {
                unlayer.loadDesign(initialDesignRef.current)
            }
        }, [getDesignAsync, getHtmlAsync, onUpdate, handleImageUpload])

        const handleReady = useCallback(() => {
            // onReady fires AFTER the initial design (if any) has loaded. UI state only.
            setReady(true)
        }, [])

        // ORB-466: merge tags now come from the web's variable catalog, which resolves over the
        // network and usually lands AFTER this iframe has mounted. `options.mergeTags` is read
        // once at init, so a late catalog would leave the menu empty forever. Push updates
        // through Unlayer's setMergeTags instead. Gated on `ready` because the editor instance
        // isn't addressable until then.
        useEffect(() => {
            if (!ready) return
            editorRef.current?.editor?.setMergeTags(mergeTags)
        }, [ready, mergeTags])

        // Disable interaction when not editable by overlaying a transparent shield.
        // Unlayer's iframe doesn't expose a "readonly" mode in the embed SDK directly;
        // the overlay is a pragmatic substitute for the asset-review/locked states.
        const shieldStyle: preact.JSX.CSSProperties = !editable
            ? { position: 'absolute', inset: 0, zIndex: 5, cursor: 'not-allowed' }
            : { display: 'none' }

        return (
            <div ref={wrapperRef} className={`relative h-full w-full overflow-hidden ${className ?? ''}`}>
                <UnlayerEditor
                    ref={editorRef}
                    onLoad={handleLoad}
                    onReady={handleReady}
                    minHeight={measuredHeight}
                    style={{ height: `${measuredHeight}px`, width: '100%' }}
                    options={{
                        // Pull Unlayer's latest channel. `stable` (1.402.0) doesn't understand
                        // schema constants emitted by their own current sample/docs and was
                        // producing migration errors against valid designs.
                        version: 'latest',
                        displayMode: 'email',
                        // ORB-290: paid-plan projectId. Required for Unlayer's newer builds to
                        // honor registerCallback('image', ...) — without it, uploads silently
                        // route to assets.unlayer.com despite the callback being registered.
                        // The project's File Storage must also be set to "Custom" in the Unlayer
                        // console; the projectId alone isn't enough.
                        ...(UNLAYER_PROJECT_ID ? { projectId: UNLAYER_PROJECT_ID } : {}),
                        appearance: {
                            theme: 'modern_light',
                            panels: {
                                tools: { dock: 'left' },
                            },
                        },
                        mergeTags,
                        // ORB-466: typing `{` in any text block opens Unlayer's merge-tag
                        // autocomplete. Unlayer exposes no cursor API to us across the iframe
                        // boundary, so this and its merge-tag menu are the only ways to insert
                        // a token into the body without leaving the keyboard.
                        mergeTagsConfig: {
                            autocompleteTriggerChar: '{',
                            sort: false,
                        },
                    }}
                />

                {/* Read-only shield */}
                <div style={shieldStyle} aria-hidden="true" />

                {/* Loading overlay until Unlayer's iframe initializes (can take ~1s).
                    Fully opaque (no /90) so Unlayer's own internal loading spinner inside
                    the iframe doesn't bleed through faintly and visually double up. */}
                {!ready && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900 z-10">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                            <span className="text-sm text-slate-500">Loading Unlayer editor…</span>
                        </div>
                    </div>
                )}
            </div>
        )
    },
)
